import React, { useState, useContext, useMemo, useEffect, useCallback } from "react";

import Grid from "@mui/material/Grid";
import CustomSelect from "shared/components/FormElements/Select";
import Input from "shared/components/FormElements/Input";
import { useHttpClient } from "shared/hooks/http-hook";
import { useForm } from "shared/hooks/form-hook";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { AuthContext } from "shared/context/auth-context";
import PhoneNumberInput from "components/ui/input/InputPhoneNumber";
import { COUNTRY_OPTIONS, getLocalizedCountryName } from "@routebot/constants";
import { getTimezoneOptions } from "shared/utility/date";
import { useTranslation } from "react-i18next";
import FormFooter from "shared/components/FormElements/FormFooter";

const ProfileSettings = () => {
  const auth = useContext(AuthContext);
  const { isLoading, sendRequest } = useHttpClient();
  const { t } = useTranslation();

  const initialCountryCode = auth?.countryCode || "TR";
  const initialTimezone = auth?.timezone || "Europe/Istanbul";
  const initialName = auth?.name || auth?.client?.name || "";
  const initialEmail = auth?.email || auth?.client?.email || "";
  const initialAddress = auth?.client?.address || "";
  const initialPhone = auth?.client?.phonenumber || "";
  //const initialTimezones = auth?.timezones || [];
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const [formState, inputHandler, setFormData] = useForm(
    {
      name: { value: initialName, isValid: initialName.trim().length > 0 },
      timezone: { value: initialTimezone, isValid: true },
      country: { value: initialCountryCode, isValid: true },
      address: { value: initialAddress, isValid: true },
      email: { value: initialEmail, isValid: true },
    },
    true
  );

  useEffect(() => {
    setFormData(
      {
        name: { value: initialName, isValid: initialName.trim().length > 0 },
        timezone: { value: initialTimezone, isValid: true },
        country: { value: initialCountryCode, isValid: true },
        address: { value: initialAddress, isValid: true },
        email: { value: initialEmail, isValid: true },
      },
      true
    );
    // eslint-disable-next-line
  }, [auth?.client]);

  const onPhoneChange = (value, cc, valid) => {
    setPhoneNumber(value);
    setCountryCode(cc);
    setIsPhoneValid(valid);
  };

  const timezoneInitial = useMemo(() => {
    const tz = formState.inputs.timezone.value || initialTimezone;
    //const found = getTimezoneOptions().find((o) => o.value === tz);
    const found = auth?.timezones?.find((o) => o.value === tz);
    return found || { value: tz, label: tz };
  }, [formState.inputs.timezone.value, initialTimezone, auth?.timezones]);

  const countryInitial = useMemo(() => {
    const cc = formState.inputs.country.value || initialCountryCode;
    const label = getLocalizedCountryName(cc) || cc;
    return { value: cc, label: `${label}` };
  }, [formState.inputs.country.value, initialCountryCode]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        const payload = {
          name: formState.inputs.name.value,
          timezone: formState.inputs.timezone.value,
          country: getLocalizedCountryName(formState.inputs.country.value) || formState.inputs.country.value,
          countryCode: formState.inputs.country.value,
          address: formState.inputs.address.value,
          phonenumber: phoneNumber,
        };

        await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/${auth.id}`, "PATCH", JSON.stringify(payload), {
          "Content-Type": "application/json",
        });

        if (window?.toast?.success) window.toast.success(t("updateSuccess"));
        if (auth?.login) {
          await auth.login(auth.id, auth.token);
        }
      } catch (err) {
        if (window?.toast?.error) window.toast.error(t("actionFailed"));
      }
    },
    [formState.inputs, phoneNumber, auth, sendRequest]
  );

  return (
    <React.Fragment>
      <form className="centered-form-padding">
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <h2>{t("yourProfile")}</h2>
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              id="email"
              element="input"
              type="email"
              label={t("yourEmail")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("invalidEmail")}
              initialValue={formState.inputs.email.value}
              initialValid={true}
              onInput={inputHandler}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              id="name"
              element="input"
              type="text"
              label={t("yourName")}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("requireField", { item: t("name") })}
              initialValue={formState.inputs.name.value}
              initialValid={true}
              onInput={inputHandler}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomSelect
              id="timezone"
              label={t("timezone") || "Timezone"}
              //options={getTimezoneOptions()}
              options={auth?.timezones}
              onInput={inputHandler}
              initialValue={timezoneInitial}
              initialValid={true}
              description={t("timezoneDescription")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomSelect
              id="country"
              label={t("country") || "Country"}
              options={COUNTRY_OPTIONS}
              onInput={inputHandler}
              initialValue={countryInitial}
              initialValid={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneNumberInput
              id="phonenumber"
              label={t("yourPhoneNumber")}
              phoneNumber={phoneNumber}
              countryCode={formState.inputs.country.value || countryCode}
              onChange={onPhoneChange}
              errorText={t("phoneNumberorEmpty")}
              language={(window?.localStorage?.i18nextLng || "en").substring(0, 2)}
              description={t("yourPhoneNumberDescription")}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              id="address"
              element="input"
              type="text"
              label={t("yourAddress")}
              validators={[VALIDATOR_NONE()]}
              initialValue={formState.inputs.address.value}
              initialValid={true}
              onInput={inputHandler}
              description={t("yourAddressDescription")}
            />
          </Grid>
          <Grid item xs={12}>
            <FormFooter
              isNewItem={true}
              onUpdate={onSubmit}
              // onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || !isPhoneValid || isLoading}
            />
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

export default ProfileSettings;
